const IMAGING_ASSET_URL_SERVER_TTL_SECONDS = 3600;

export function mergeOffsetLimitResult(listKey = "items") {
  return (existing: any = {}, incoming: any = null) => {
    if (!incoming) {
      return existing;
    }
    const merged = existing[listKey] ? existing[listKey].slice(0) : [];
    const start = incoming.offset;
    const end = start + incoming[listKey].length;
    for (let i = start; i < end; ++i) {
      merged[i] = incoming[listKey][i - start];
    }

    return {
      ...incoming,
      [listKey]: merged,
    };
  };
}

/**
 * Handles offset based pagination for queries which
 * those return shape is
 * { items: any[], offset: number, limit: number }
 */
function offsetLimitPaginatedResult(keyArgs) {
  return {
    keyArgs,
    merge: mergeOffsetLimitResult(),
  };
}

export const apolloCacheTypePolicies = {
  Query: {
    fields: {
      // Cache presigned URLs keyed by identifying inputs and respect TTL
      generateImagingAssetUrl: {
        keyArgs: (_args, { variables }) => {
          const input = variables?.input ?? {};
          return JSON.stringify({
            imagingInstanceId: input.imagingInstanceId,
            kind: input.kind,
          });
        },
        read(existing) {
          if (!existing) return undefined;
          const now = Date.now();
          const SAFETY_BUFFER_MS = 5000; // refresh shortly before expiry
          if (
            existing._expiresAt &&
            now < existing._expiresAt - SAFETY_BUFFER_MS
          ) {
            return existing;
          }
          return undefined; // force network fetch when missing or expired
        },
        merge(_existing, incoming) {
          // api-imaging-study returns a null url while a cine is still
          // transcoding, so caching it would hide the finished loop for the TTL.
          if (!incoming?.url) return incoming;
          const now = Date.now();
          return {
            ...incoming,
            // synthetic field for read() TTL check
            _expiresAt: now + IMAGING_ASSET_URL_SERVER_TTL_SECONDS * 1000,
          };
        },
      },
      patientTriageStatuses: {
        merge: false,
      },
      investigationActivitiesPaginatedOffset: offsetLimitPaginatedResult([
        "input",
      ]),
      facilityQueues: {
        merge: false,
      },
      getDepartments: {
        keyArgs: (_, { variables: { clinicId } }) =>
          // Ensure deparments are still keyed by clinicId
          // We don't want to see the same departments for a different clinic
          JSON.stringify({ clinicId }),
        merge: mergeOffsetLimitResult("list"),
        read: (
          existing,
          {
            args: {
              input: { pagination },
            },
          },
        ) => {
          // A read function should always return undefined if existing is
          // undefined. Returning undefined signals that the field is
          // missing from the cache, which instructs Apollo Client to make a network request
          if (!existing || !existing.list) {
            return existing;
          }

          const serverHasMoreItemsThanCurrentPaginationLimit =
            pagination.total > pagination.limit &&
            existing.list?.length < pagination.limit;

          if (serverHasMoreItemsThanCurrentPaginationLimit) {
            return undefined; // force a refetch
          }

          return {
            ...existing,
            list: existing.list.slice(
              pagination.offset,
              pagination.offset + pagination.limit,
            ),
          };
        },
      },
    },
  },
  VisitLocationSnapshot: {
    keyFields: (o) => `${o._id}-${o.created}`,
  },
  VisitDepartmentSnapshot: {
    keyFields: (o) => `${o._id}-${o.careEventCreatedAt}`,
  },
  VisitCareEvent: {
    keyFields: ["_id", "type"],
  },
  VisitInvestigationActivity: {
    keyFields: ["_id", "outcome"],
  },
  VisitPatientVital: {
    keyFields: (o) => `${o.__typename}:${o.visitId}:${o.id}`,
  },
  User: {
    fields: {
      person: {
        merge: false,
      },
    },
  },
  Patient: {
    fields: {
      consultations: {
        merge(
          existingReadOnly: { id: string }[],
          incoming: { id: string }[],
          { readField },
        ) {
          // This merge function results in the incoming consultation taking priority and ensures
          // there are no duplicates
          const existing = existingReadOnly ? existingReadOnly.slice(0) : [];
          // Obtain a Set of all existing consultation IDs.
          const incomingIdSet = new Set(
            incoming.map((consultation) => readField("id", consultation)),
          );
          // Remove incoming consultations present in the existing data.
          const existingFiltered = existing.filter(
            (task) => !incomingIdSet.has(readField("id", task)),
          );

          return [...existingFiltered, ...incoming];
        },
      },
    },
  },
  PatientQueue: {
    fields: {
      patients: {
        merge: false,
      },
    },
  },
};
